import { isGraphApiError } from 'src/shared/state/api-slices/graphApiError';

const NOT_FOUND_CODES = ['PROJECT_NOT_FOUND', 'CUSTOMER_NOT_FOUND'];

/** The two authorisation directives reject before any resolver runs, and send no error code. */
const REFUSED_AT_THE_DOOR = [/not in the required group/i, /not an admin/i];

const NO_PERMISSION = 'You no longer have permission to do this, so nothing was deleted.';

const isForbidden = (error: unknown): boolean => typeof error === 'object' && error !== null && 'status' in error && error.status === 403;

/** The engine archive runs before the cascade, so when it fails nothing has been deleted yet. */
export const describeArchiveFailure = (error: unknown): string =>
    isForbidden(error)
        ? NO_PERMISSION
        : 'The Nature Risk organisation could not be archived, so nothing was deleted. It is safe to try again.';

/** The purge runs after the archive and before the cascade, so only the archive has happened. */
export const PURGE_FAILURE =
    'The Nature Risk organisation was archived but could not be permanently removed, so the customer was not deleted. It is safe to try again.';

/**
 * For when the cascade is refused after the Nature Risk step went through, so "nothing was deleted"
 * would be false.
 */
export const REFUSED_AFTER_NATURE_RISK: Record<'archived' | 'purged', string> = {
    archived:
        'You no longer have permission to do this, so the customer was not deleted. Its Nature Risk organisation had already been archived.',
    purged: 'You no longer have permission to do this, so the customer was not deleted. Its Nature Risk data had already been permanently removed.',
};

export type DeleteFailure = {
    message: string;
    /** The record could not be found, so the list should be re-read rather than the delete retried. */
    alreadyGone: boolean;
    /** Refused before any resolver ran, so the cascade itself deleted nothing. */
    refused: boolean;
};

/**
 * Turns a failed delete into something an operator can act on. The cascade spans several stores and
 * does not roll back, so an unrecognised failure must never read as "nothing happened" — some of the
 * record may be gone already, and re-running is what completes the rest.
 */
export const describeDeleteFailure = (error: unknown): DeleteFailure => {
    const failures = isGraphApiError(error) ? error.failures ?? [] : [];

    if (failures.some(failure => failure.code && NOT_FOUND_CODES.includes(failure.code))) {
        return {
            alreadyGone: true,
            refused: false,
            message:
                'That could not be found. It may already have been deleted, or the graph may be briefly unavailable — the list has been re-read, so check before trying again.',
        };
    }

    if (failures.some(failure => REFUSED_AT_THE_DOOR.some(pattern => pattern.test(failure.message ?? '')))) {
        return {
            alreadyGone: false,
            refused: true,
            message: NO_PERMISSION,
        };
    }

    return {
        alreadyGone: false,
        refused: false,
        message:
            'That did not finish, and it may have partly completed — there is no rollback. It is safe to try again: the delete picks up whatever is left.',
    };
};
